import React, { useState } from 'react';
import { Button, FormControl, Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import jimboPalette from 'src/jimboTheme/palette';
import { FieldError, FieldValues, Path, useForm, UseFormProps } from 'react-hook-form';
import { useAppSelector } from '@hooks/redux-custom';
import { ResetModal } from '@components';
import { useCustomer } from '@hooks/useCustomer';

interface Props<T extends FieldValues> {
  onSave: (formData: T) => void;
  /**
   * Props for useForm hook
   */
  useFormProps: UseFormProps<T>;
  /**
   * Label to display when no default value is provided.
   */
  labelWhenNoDefaultValue?: string;
  customWidth?: number;
  changeGridLayout?: any;
  textProps?: any;
  textFieldLabels?: any;
  customRow?: number;
  disableEditing?: boolean;
  disabledEditingTooltip?: string;
  isIDReset?: boolean;
}

export const EditableText = <T extends FieldValues>({
  onSave,
  labelWhenNoDefaultValue,
  useFormProps,
  customWidth,
  changeGridLayout,
  textProps,
  disableEditing,
  disabledEditingTooltip,
  isIDReset = false,
}: Props<T>) => {
  const [editing, setEditing] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const { customer, customerAttributes } = useCustomer();
  const [formData, setFormData] = useState<T>({} as T);

  const activeUser = useAppSelector((state) => state.activeUser.value);
  const userAccessStatus = activeUser?.user?.roles.find((role: any) => role.moduleName === 'customer');
  const hasWriteAccess = userAccessStatus.role > 1;
  const canEdit = hasWriteAccess && editing;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<T>(useFormProps);

  const onSaveClick = (fData: T) => {
    if (isIDReset) {
      setIsResetModalOpen(true);
      setFormData(fData);
    } else {
      onSave(fData);
      setEditing(false);
    }
  };

  const onCancelClick = () => {
    reset();
    setEditing(false);
  };

  const label =
    useFormProps.defaultValues &&
    Object.values(useFormProps.defaultValues)
      .map((value) => value)
      .join(' ');

  return (
    <>
      <ResetModal
        isOpen={isResetModalOpen}
        customer={customer}
        onClose={(isCancelled) => {
          if (!isCancelled) {
            onSave(formData);
          }

          setEditing(false);
          setIsResetModalOpen(false);
        }}
        resetBtnLabel="Continue"
        bigCCustomerAttributes={customerAttributes}
        modalContent={{
          attributesToDelete: ['IDVerified', 'IDVerifiedOn', 'idVerificationData'],
          header: 'Change details',
          body: 'Please advise the customer that changing their name or date of birth will wipe the ID Verified status from their account, and they will need to verify again',
        }}
      />
      {useFormProps ? (
        canEdit ? (
          <form onSubmit={handleSubmit((fData) => onSaveClick(fData as T))} data-testid="editable-text-form">
            <Grid container alignItems="center" spacing={2}>
              {useFormProps.defaultValues &&
                Object.keys(useFormProps.defaultValues).map((key, index) => {
                  const error = Object.entries(errors).find(([k, v]) => k === key)?.[1] as FieldError;
                  return (
                    <Grid item key={key} xs={changeGridLayout ? changeGridLayout : 'auto'}>
                      <FormControl>
                        <TextField
                          sx={{ width: customWidth }}
                          multiline
                          {...register(key as Path<T>)}
                          name={key}
                          error={!!error}
                          helperText={error?.message}
                          data-testid="editable-text-input"
                          {...textProps[index]}
                        />
                      </FormControl>
                    </Grid>
                  );
                })}
              <Grid item xs="auto">
                <Button variant="contained" type="submit" data-testid="save-button" sx={{ width: '80px' }}>
                  Save
                </Button>
              </Grid>
              <Grid item xs="auto">
                <Button variant="outlined" onClick={onCancelClick} sx={{ width: '80px' }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        ) : (
          <Grid container spacing={2} alignItems="center" data-testid="editable-text">
            <Grid item>
              <Typography onClick={() => !disableEditing && setEditing(true)} sx={{ cursor: disableEditing ? 'text' : 'pointer' }}>
                {label ? label : textProps?.placeholder ? textProps.placeholder : 'Click to Edit'}
              </Typography>
            </Grid>
            <Grid item>
              {hasWriteAccess && (
                <Tooltip title={disableEditing ? disabledEditingTooltip ?? '' : ''} placement="top">
                  <span>
                    <IconButton size="small" disabled={disableEditing} onClick={() => setEditing(true)}>
                      <EditIcon fontSize="small" sx={{ cursor: 'pointer', '&:hover': { color: jimboPalette.primary.main } }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        )
      ) : (
        <></>
      )}{' '}
    </>
  );
};
